
import React from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Tractor, LayoutGrid, ShoppingCart, Calendar, BarChart2, BarChart3, ShoppingBag, GitCompare, Database } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const PriceBenchmarking: React.FC = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();

  const priceModules = [
    {
      title: 'Overview',
      description: 'Comprehensive price analysis and market intelligence',
      icon: BarChart3,
      path: '/price-benchmarking/overview',
      color: 'bg-indigo-100 text-indigo-700',
      allowedRoles: ['admin', 'finance', 'warehouse', 'seller'],
    },
    {
      title: 'Benchmark',
      description: 'Compare prices across different market sources',
      icon: TrendingUp,
      path: '/price-benchmarking/benchmark',
      color: 'bg-yellow-100 text-yellow-700',
      allowedRoles: ['admin', 'finance', 'warehouse', 'seller'],
    },
    {
      title: 'Farm Prices',
      description: 'Track and manage prices directly from farms',
      icon: Tractor,
      path: '/price-benchmarking/farm-prices',
      color: 'bg-green-100 text-green-700',
      allowedRoles: ['admin', 'finance', 'warehouse', 'seller'],
    },
    {
      title: 'Distribution Center',
      description: 'Monitor distribution center pricing data',
      icon: LayoutGrid,
      path: '/price-benchmarking/distribution-center',
      color: 'bg-blue-100 text-blue-700',
      allowedRoles: ['admin', 'finance', 'warehouse', 'seller'],
    },
    {
      title: 'Local Shops',
      description: 'Collect pricing from local shop sources',
      icon: ShoppingCart,
      path: '/price-benchmarking/local-shops',
      color: 'bg-purple-100 text-purple-700',
      allowedRoles: ['admin', 'finance', 'warehouse', 'seller'],
    },
    {
      title: 'Sunday Market',
      description: 'Weekend market price tracking',
      icon: Calendar,
      path: '/price-benchmarking/sunday-market',
      color: 'bg-orange-100 text-orange-700',
      allowedRoles: ['admin', 'finance', 'warehouse', 'seller'],
    },
    {
      title: 'Supermarket',
      description: 'Supermarket retail price monitoring',
      icon: BarChart2,
      path: '/price-benchmarking/supermarket',
      color: 'bg-red-100 text-red-700',
      allowedRoles: ['admin', 'finance', 'warehouse', 'seller'],
    },
    {
      title: 'Ecommerce',
      description: 'Online marketplace and ecommerce price tracking',
      icon: ShoppingBag,
      path: '/price-benchmarking/ecommerce',
      color: 'bg-cyan-100 text-cyan-700',
      allowedRoles: ['admin', 'finance', 'warehouse', 'seller'],
    },
    {
      title: 'Multi-Product Comparison',
      description: 'Compare multiple products across different market sources',
      icon: GitCompare,
      path: '/price-benchmarking/multi-product-comparison',
      color: 'bg-purple-100 text-purple-700',
      allowedRoles: ['admin', 'finance', 'warehouse', 'seller'],
    },
    {
      title: 'ChipChip Sales Data',
      description: 'Sync and manage sales data from ChipChip platform',
      icon: Database,
      path: '/price-benchmarking/chipchip-sales-data',
      color: 'bg-teal-100 text-teal-700',
      allowedRoles: ['admin', 'finance', 'warehouse', 'seller'],
    },
    {
      title: 'B2B Sales Data',
      description: 'Sync and manage delivery data from B2B platform',
      icon: Database,
      path: '/price-benchmarking/b2b-sales-data',
      color: 'bg-amber-100 text-amber-700',
      allowedRoles: ['admin', 'finance', 'warehouse', 'seller'],
    },
  ];

  // Filter modules based on user role
  const filteredModules = priceModules.filter(module => 
    module.allowedRoles.includes(userRole || '')
  );

  return (
    <Layout>
      <div className="w-full">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp className="h-8 w-8 text-green-600" />
            <h1 className="text-3xl font-bold text-gray-900">Price Benchmarking</h1>
          </div>
          <p className="text-gray-600">
            Comprehensive price collection and benchmarking across multiple sources
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredModules.map((module) => {
            const IconComponent = module.icon;
            return (
              <Card key={module.path} className="cursor-pointer hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-lg ${module.color}`}>
                      <IconComponent className="h-6 w-6" />
                    </div>
                    <CardTitle className="text-lg">{module.title}</CardTitle>
                  </div>
                  <CardDescription>{module.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={() => navigate(module.path)}
                    className="w-full"
                    variant="outline"
                  >
                    View {module.title}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

export default PriceBenchmarking;
